import type { ComponentPublicInstance, ExtractPropTypes, PropType } from "vue";

type FeatureTypeEnum = "Areas" | "Devices" | "Facilities" | "Cameras";

// OpenLayers 相关类型定义
// 说明：ol 库真实类型（Feature/Map/View/VectorSource 等）结构复杂，
// 手写简化类型会与 ol 的强类型冲突，这里统一用 any 以兼容真实 ol 对象。
type OpenLayersFeature = any;
type OpenLayersGeometry = any;
type OpenLayersMap = any;
type OpenLayersView = any;
type OpenLayersVectorSource = any;

interface OpenLayersFormat {
  GeoJSON: {
    new (): {
      readFeatures: (geojson: any) => OpenLayersFeature[];
      writeFeatures: (features: OpenLayersFeature[]) => string;
    };
  };
}

interface OpenLayersFeatureConstructor {
  new (properties: Record<string, any>): OpenLayersFeature;
}

interface OpenLayersGeometryConstructor {
  Point: {
    new (coordinates: number[]): OpenLayersGeometry;
  };
  Polygon: {
    new (coordinates: number[][][]): OpenLayersGeometry;
  };
}

// 地图组件相关类型
interface MapComponent {
  getMap: () => OpenLayersMap;
  getVectorSource: () => OpenLayersVectorSource;
  getView: () => OpenLayersView;
  calculateAutoZoom: () => void;
  createIcon: (iconUrl: string, scale: number) => any;
}

// 事件相关类型
interface DragEvent {
  screenX: number;
  screenY: number;
  [key: string]: any;
}

interface MapPointerEvent {
  pixel: number[];
  originalEvent: any;
}

// 颜色选择器类型
interface ColorPickerValue {
  rgba: {
    r: number;
    g: number;
    b: number;
    a: number;
  };
  hex?: string;
  hsl?: {
    h: number;
    s: number;
    l: number;
  };
}

// 楼层组件类型
interface FloorComponent {
  init: (floors: Floor[], defaultFloor: Floor) => void;
}

// 拖拽图标组件类型
interface DragIconComponent {
  icon: string;
  item: Device;
  mapRef: MapComponent;
  move1: number[];
}

// 扩展原有的接口定义
interface Device {
  code: string;
  deviceType?: string;
  deviceTypeName?: string;
  videoName?: string;
  remark?: string;
  floor?: string | number;
  [key: string]: any;
}

interface Floor {
  floor: string | number;
  bucketName?: string;
  geojson?: any;
  videoGeoJson?: any;
  facilityGeoJson?: any;
  [key: string]: any;
}

interface ConfigIcon {
  [code: string]: string;
}

interface ConfigTopBarItem {
  name: string;
  icon: string;
  value: string;
}

interface ConfigBottomBarItem {
  name: string;
  icon: string;
}

interface MenuItem {
  text: string;
  icon: string;
  callback: (val: any) => void;
  classname?: string;
}

interface OptionType {
  label: string;
  value: string;
  key: string;
}

interface QueryDeviceListType {
  activatedDeviceType: string;
  activatedManufacturer: string;
  activatedDeviceModel: string;
}

interface OpenParams {
  listFloor: Floor[];
  listDevice: Device[];
  mode?: string;
  defaultFloor?: Floor | null;
  waring?: string | number | null;
  listTitle?: string;
  configIcon?: ConfigIcon;
  configTopBar?: ConfigTopBarItem[];
  type?: string;
  featureType?: FeatureTypeEnum[] | [];
}

interface MapEvent {
  pixel: number[];
  coordinate: number[];
  feature?: OpenLayersFeature;
  geometry?: OpenLayersGeometry;
}

interface DrawEvent {
  feature: OpenLayersFeature;
  type?: string;
}

interface StyleUpdateParams {
  feature?: OpenLayersFeature;
  isSelected?: boolean;
  offset?: number;
}

interface SaveResult {
  geojson: string;
  floor: Floor | undefined;
}

// 组件事件类型
interface ComponentEmits {
  closeMain: [];
  saveMain: [result: SaveResult];
}

// 组件Props类型
interface ComponentProps {
  type?: string;
}

// 注入的类型
interface Injections {
  "ol-format": OpenLayersFormat;
  "ol-feature": OpenLayersFeatureConstructor;
  "ol-geom": OpenLayersGeometryConstructor;
}

export type {
  ColorPickerValue,
  ComponentEmits,
  ComponentProps,
  ConfigBottomBarItem,
  ConfigIcon,
  ConfigTopBarItem,
  Device,
  DragEvent,
  DragIconComponent,
  DrawEvent,
  FeatureTypeEnum,
  Floor,
  FloorComponent,
  Injections,
  MapComponent,
  MapEvent,
  MapPointerEvent,
  MenuItem,
  OpenLayersFeature,
  OpenLayersFeatureConstructor,
  OpenLayersFormat,
  OpenLayersGeometry,
  OpenLayersGeometryConstructor,
  OpenLayersMap,
  OpenLayersVectorSource,
  OpenLayersView,
  OpenParams,
  OptionType,
  QueryDeviceListType,
  SaveResult,
  StyleUpdateParams,
};

export const GeoMarkProps = {
  type: { type: String as PropType<string>, default: "" },
};

export type GeoMarkPropsType = ExtractPropTypes<typeof GeoMarkProps>;
export type GeoMarkInstance = ComponentPublicInstance & {
  open: (params: OpenParams) => Promise<void>;
};
